import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { SupplierEditDraft, SupplierRow } from './SupplierList.types'

export interface EditSupplierDrawerProps {
  open: boolean
  onClose: () => void
  supplier: SupplierRow | null
  typeOptions: string[]
  onSave: (patch: Partial<SupplierRow>) => void
  onDelete: () => void
}

export function EditSupplierDrawer({
  open,
  onClose,
  supplier,
  typeOptions,
  onSave,
  onDelete,
}: EditSupplierDrawerProps) {
  const [draft, setDraft] = useState<SupplierEditDraft>({
    name: '',
    type: '',
    documentsUrl: '',
    blanket: false,
    authorizedOems: [],
  })

  useEffect(() => {
    if (!open) return
    setDraft({
      name: supplier?.name ?? '',
      type: supplier?.type ?? '',
      documentsUrl: supplier?.documentsUrl ?? '',
      blanket: supplier?.blanket ?? false,
      authorizedOems: supplier?.authorizedOems ?? [],
    })
  }, [open, supplier])

  const canSave = useMemo(() => {
    return draft.name.trim().length > 0 && draft.type.trim().length > 0
  }, [draft.name, draft.type])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: 'clamp(420px, 40vw, 720px)', display: 'flex', flexDirection: 'column' },
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Supplier
        </Typography>

        <IconButton onClick={onClose} size="small" aria-label="Close supplier drawer">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        <Box sx={{ bgcolor: '#f3f4f6', p: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              size="small"
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Type"
              size="small"
              select
              value={draft.type}
              onChange={e => setDraft(d => ({ ...d, type: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">
                <em>Type</em>
              </MenuItem>
              {typeOptions.map(t => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Documents URL"
              size="small"
              value={draft.documentsUrl}
              onChange={e => setDraft(d => ({ ...d, documentsUrl: e.target.value }))}
              fullWidth
            />

            <FormControlLabel
              label="Blanket"
              control={
                <Checkbox
                  checked={draft.blanket}
                  onChange={e => setDraft(d => ({ ...d, blanket: e.target.checked }))}
                />
              }
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              disabled={!canSave}
              onClick={() => {
                onSave({
                  name: draft.name.trim(),
                  type: draft.type.trim(),
                  blanket: draft.blanket,
                  documentsUrl: draft.documentsUrl.trim(),
                })
              }}
            >
              SAVE
            </Button>
            <Button variant="outlined" color="error" onClick={onDelete}>
              DELETE
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}
